import { sanityClient } from './sanityClient';
import { ALL_PACKAGES, PACKAGE_BY_SLUG } from './queries';
import type { TourDetail } from '../data/tourData';
import { mapSanityToTourDetail, type RelatedPackage, type SanityTourDetail } from '../hooks/useTourPackage';

export interface SanitySlug {
  current: string;
}

export interface SanityImageRef {
  _type: 'image';
  asset: { _ref: string; _type: 'reference' };
  alt?: string;
}

export interface SanityTourPackage {
  _id: string;
  _updatedAt: string;
  title: string;
  slug: SanitySlug;
  destination: string;
  region?: string;
  theme?: string[];
  duration_days: number;
  duration_nights: number;
  price_per_person_aed: number;
  is_featured: boolean;
  is_published: boolean;
  hotel_name?: string;
  hotel_stars?: number;
  hero_image: SanityImageRef;
  inclusions?: string[];
  description?: string;
  highlights?: string[];
}

// When SSG_SKIP_SANITY=1 is set (e.g. a CI/verification build in a sandbox that
// cannot reach api.sanity.io), skip all build-time Sanity fetches and fall back
// to local data. This keeps `npm run build` from hard-crashing on a blocked
// network egress (a 403 CONNECT raises an unhandled socket error that bypasses
// try/catch). Normal builds (local, GitHub Actions) do NOT set this flag, so
// their behaviour is unchanged.
function skipSanityAtBuild(): boolean {
  return typeof process !== 'undefined' && !!process.env && process.env.SSG_SKIP_SANITY === '1';
}

export function sortTourPackages(packages: SanityTourPackage[] = []) {
  return [...packages].sort((a, b) => {
    if (b.is_featured !== a.is_featured) return b.is_featured ? 1 : -1;
    return (a.price_per_person_aed ?? Number.MAX_SAFE_INTEGER) - (b.price_per_person_aed ?? Number.MAX_SAFE_INTEGER);
  });
}

export async function fetchTourPackagesFromSanity() {
  if (skipSanityAtBuild()) return [];
  const data = await sanityClient.fetch<SanityTourPackage[]>(ALL_PACKAGES);
  return sortTourPackages(data ?? []);
}

export async function fetchTourPackageStaticPaths(fallbackSlugs: string[]) {
  if (skipSanityAtBuild()) return fallbackSlugs.map(slug => `tour-packages/${slug}`);
  try {
    const sanityPackages = await fetchTourPackagesFromSanity();
    const sanitySlugs = sanityPackages
      .map(pkg => pkg.slug?.current)
      .filter((slug): slug is string => Boolean(slug));

    return Array.from(new Set([...fallbackSlugs, ...sanitySlugs])).map(slug => `tour-packages/${slug}`);
  } catch (error) {
    console.warn('Sanity tour package static paths unavailable:', error);
    return fallbackSlugs.map(slug => `tour-packages/${slug}`);
  }
}

export interface TourPackageDetailLoaderData {
  tour: TourDetail | null;
  related: RelatedPackage[];
}

export async function fetchTourPackageDetailLoaderData(slug = ''): Promise<TourPackageDetailLoaderData> {
  if (!slug) return { tour: null, related: [] };
  if (skipSanityAtBuild()) return { tour: null, related: [] };

  try {
    const data = await sanityClient.fetch<SanityTourDetail | null>(PACKAGE_BY_SLUG, { slug });
    if (!data) return { tour: null, related: [] };

    const related = await sanityClient.fetch<RelatedPackage[]>(
      `*[_type == "tourPackage" && slug.current != $slug && is_published == true && (
        destination == $destination || region == $region || count((theme[])[@ in $theme]) > 0
      )] | order(is_featured desc, price_per_person_aed asc)[0...3] {
        _id, title, slug, destination, region, theme,
        duration_days, duration_nights, price_per_person_aed,
        hero_image, hotel_stars, is_featured
      }`,
      { destination: data.destination, region: data.region ?? '', theme: data.theme ?? [], slug }
    );

    return {
      tour: mapSanityToTourDetail(data),
      related: related ?? [],
    };
  } catch (error) {
    console.warn('Sanity tour package detail unavailable:', error);
    return { tour: null, related: [] };
  }
}
